import { useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import DottedTexture from '@/assets/branding/dotted-texture.svg?react';
import Logo from '@/assets/branding/logo.svg?react';
import { Dialog, DialogContent } from '@/components/ui';
import Copyable from '@/components/ui/copyable';
import useReferrals from '@/features/referrals/api/get-referrals';
import useRewards from '@/features/referrals/api/get-rewards';
import { useModalStore } from '@/store/use-modal-store';

export default function ReferralLinkModal() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: referrals } = useReferrals();
  const { data: rewardsData } = useRewards();

  const isOpen = useModalStore((state) => state.isReferralLinkModalOpen);
  const isOpenByQueryParam = searchParams.get('modal') === 'referral';

  const referralLink = `https://trade.vestmarkets.com/join/${referrals?.ref_code}`;

  useEffect(() => {
    if (!isOpenByQueryParam) {
      return;
    }

    useModalStore.setState({ isReferralLinkModalOpen: true });

    const params = new URLSearchParams(searchParams);
    params.delete('modal');
    const search = params.toString();

    navigate({ search: search ? `?${search}` : '' }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpenByQueryParam]);

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) =>
        useModalStore.setState({ isReferralLinkModalOpen: open })
      }
    >
      <DialogContent className="gap-10 overflow-hidden border-none bg-card sm:max-w-[440px] sm:!rounded-3xl">
        <div className="relative flex flex-col gap-5 p-5 pb-0 pt-8 sm:p-14 sm:pb-0">
          <DottedTexture className="pointer-events-none absolute top-0 z-[-1] h-auto w-full scale-[1.7] opacity-50 [&_ellipse]:fill-[#65B482]" />

          <div className="text-center text-4xl font-semibold capitalize leading-[130%]">
            <Logo className="mx-auto mb-8 h-auto w-[65px] [&_path]:fill-foreground" />

            {rewardsData?.next_referral_discount === 100
              ? t('capitalResultModal.getFreeRetry')
              : t('capitalResultModal.getADiscoutForNextAccount')}
          </div>

          <div className="text-center text-xl text-muted-foreground">
            {rewardsData?.next_referral_discount === 100
              ? t('capitalResultModal.getFreeRetryMessage')
              : t('capitalResultModal.getADiscoutForNextAccountMessage', {
                  discount: rewardsData?.next_referral_discount ?? 20,
                })}
          </div>
        </div>

        <div className="flex flex-col gap-3 p-5 pt-0 sm:p-14 sm:pt-0">
          <div className="flex flex-col gap-10">
            <Copyable
              variant="button"
              text={t('portfolio.copyInviteLink')}
              buttonProps={{
                size: 'lg',
                variant: 'contrast',
                className: 'capitalize',
              }}
            >
              {referralLink}
            </Copyable>

            <div className="text-lg text-muted-foreground">
              <span className="font-semibold text-foreground">1.</span>{' '}
              <Trans
                i18nKey="capitalResultModal.getFreeRetryStep1"
                components={{
                  b: <b className="font-semibold text-foreground" />,
                }}
              />
              <br />
              <span className="font-semibold text-foreground">2.</span>{' '}
              <Trans
                i18nKey="capitalResultModal.getFreeRetryStep2"
                values={{
                  discount: rewardsData?.next_referral_discount ?? 20,
                }}
                components={{
                  b: <b className="font-semibold text-foreground" />,
                }}
              />
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
